<template>
  <div class="flex text-xl sm:text-2xl">
    <font-awesome-icon class="cursor-pointer hover:text-blue-400" :icon="['fas', 'book']"
                       @click.stop="showDictionary = true"/>
  </div>
  <Modal :is-visible="showDictionary" @update:isVisible="showDictionary = $event" heading="Dictionary">
    <!-- look up any word -->
    <div class="flex gap-2">
      <input v-model="query" @keyup.enter="search" type="text" placeholder="Look up a word..."
             class="p-2 border-2 border-blue-200 rounded w-full bg-gray-900 text-white">
      <button @click="search"
              class="bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-gray-900 px-4 rounded">
        Search
      </button>
    </div>
    <p v-if="notFound" class="text-red-300 text-sm mt-2">No definition found for "{{ notFound }}".</p>

    <!-- words looked up this session (your found words seed this list) -->
    <div v-if="entries.length" class="flex flex-row flex-wrap gap-2 mt-3">
      <button v-for="(entry, index) in entries" :key="entry.word"
              @click.stop="selectedIndex = index; notFound = ''"
              class="cursor-pointer px-2 rounded"
              :class="index === selectedIndex ? 'bg-gradient-to-r from-green-400 to-teal-400 text-gray-900' : 'bg-gray-700 text-white'">
        {{ capitalize(entry.word) }}
      </button>
    </div>

    <div v-if="searching" class="mt-6 flex justify-center">
      <LoadingSpinner/>
    </div>

    <template v-else-if="entries[selectedIndex]">
      <div v-for="meaning of entries[selectedIndex].definition.meanings" class="flex flex-col mt-2">
        <div class="text-left">{{ meaning.partOfSpeech }}</div>
        <ul>
          <li v-for="definition in meaning.definitions" class="text-left list-disc list-inside text-xs">
            {{ definition.definition }}
          </li>
        </ul>
      </div>
    </template>

    <p v-else class="text-gray-400 text-sm mt-6 text-center">Search for a word to see its definition.</p>
  </Modal>
</template>
<script setup>

import {onMounted, ref, watch} from "vue";
import Modal from "./Modal.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {useDictionary} from "../composables/useDictionary.js";

let showDictionary = ref(false);
const {getDefinition} = useDictionary();

// { word, definition } for every word looked up (found words seed this list)
const entries = ref([]);
const selectedIndex = ref(0);
const query = ref('');
const searching = ref(false);
const notFound = ref('');

const props = defineProps({
  words: Array
})

onMounted(() => syncFoundWords());

// populateGrid() assigns a fresh foundWords array each new game, so a changed
// array reference means a new game started - wipe the lookup (search included)
watch(() => props.words, () => {
  resetDictionary();
  syncFoundWords();
});

// within a game, words are pushed onto the same array as they're revealed
watch(() => props.words.length, () => syncFoundWords());

function resetDictionary() {
  entries.value = [];
  selectedIndex.value = 0;
  query.value = '';
  notFound.value = '';
}

async function syncFoundWords() {
  for (const word of props.words ?? []) {
    if (!hasWord(word)) {
      await addWord(word, false);
    }
  }
}

function hasWord(word) {
  return entries.value.some(e => e.word.toLowerCase() === word.toLowerCase());
}

async function search() {
  const word = query.value.trim();
  if (!word) return;
  await addWord(word, true);
  query.value = '';
}

// fetch a word and add it; `select` jumps to it and surfaces a not-found message
async function addWord(word, select) {
  const existing = entries.value.findIndex(e => e.word.toLowerCase() === word.toLowerCase());
  if (existing !== -1) {
    if (select) selectedIndex.value = existing;
    return;
  }

  searching.value = true;
  notFound.value = '';
  let definition = null;
  try {
    const result = await getDefinition(word);
    if (result && result.length > 0) {
      definition = result[0];
    }
  } catch (error) {
    console.error('Error fetching definition for ', word, error);
  }
  searching.value = false;

  if (!definition) {
    // a searched word that doesn't exist just gets a message; a found word
    // (select=false) still gets a placeholder so it stays reviewable
    if (select) {
      notFound.value = word;
      return;
    }
    definition = fallbackDefinition(word);
  }

  entries.value.push({word, definition});
  if (select || entries.value.length === 1) {
    selectedIndex.value = entries.value.length - 1;
  }
}

function fallbackDefinition(word) {
  return {
    meanings: [{
      partOfSpeech: 'There was an issue',
      definitions: [{definition: `There was an error fetching the definition for ${word.toLowerCase()}`}]
    }]
  };
}

function capitalize(word) {
  return `${word[0].toUpperCase()}${word.substring(1).toLowerCase()}`;
}

</script>


<style scoped>

</style>
